import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { Element, scroller } from "react-scroll";
import TimeClock from "@/public/icons/clock-orange-small.svg";
import { STEPS, StepCta } from "./stepsData";
import FaqAccordion from "./FaqAccordion";
import Link from "next/link";

const StepCtaLink = ({ cta }: { cta: StepCta }) => {
  const router = useRouter();

  const onClick =
    cta.kind === "scroll"
      ? () => scroller.scrollTo(cta.scrollTo, { smooth: true, duration: 200, offset: 0 })
      : () => router.push(cta.href);

  return (
    <Link href={cta.kind === "scroll" ? "#" : cta.href} onClick={onClick}>
      <a className="inline-block mt-7 px-6 py-3 text-base font-bold text-center text-white transition duration-300 ease-in-out transform rounded shadow bg-landing-orange  hover:scale-105">
        {cta.label}
      </a>
    </Link>
  );
};

const Journey = ({
  step1OpenFaq,
  setStep1OpenFaq,
}: {
  step1OpenFaq?: number;
  setStep1OpenFaq?: (idx: number) => void;
}) => {
  const [active, setActive] = useState(0);
  const refs = useRef<Array<HTMLDivElement | null>>([]);
  const [vpLastPixel, setVpLastPixel] = useState(0);
  const [vpCenter, setVpCenter] = useState(0);

  useEffect(() => {
    let raf: number | null = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const deskTopCardCentre = cardRef.current
          ? cardRef.current.getBoundingClientRect().top +
            cardRef.current.getBoundingClientRect().height / 2
          : 0;

        setVpLastPixel(window.innerHeight);
        const tops = refs.current.map((el) => {
          if (!el) return Infinity;
          const r = el.getBoundingClientRect();
          return r.top + 40;
        });
        setVpCenter(window.innerHeight / 2);
        setScreenValues({
          deskTopCardCentre,
          tops,
        });

        const last = tops.length - 1;
        let p = 0;
        if (deskTopCardCentre <= tops[0]) p = 0;
        else if (deskTopCardCentre >= tops[last]) p = last;
        else {
          for (let i = 0; i < last; i++) {
            if (deskTopCardCentre >= tops[i] && deskTopCardCentre < tops[i + 1]) {
              p = i;
              break;
            }
          }
        }
        setActive(Math.round(p));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const [screenValues, setScreenValues] = useState<{
    deskTopCardCentre: number;
    tops: number[];
  }>({
    deskTopCardCentre: 0,
    tops: [],
  });

  const current = STEPS[active];
  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <Element name="journey">
      <section className="pb-4 pt-0 md:pb-24 lg:pt-24 bg-white">
        <div className="flex gap-2 sticky top-10 h-[50px] items-end lg:hidden px-2 z-20">
          {STEPS.map((_, i) => (
            <div key={i} className="flex-1 h-[5px] rounded-full bg-landing-gray overflow-hidden ">
              <div
                className="h-full bg-landing-orange rounded-full origin-left"
                style={{
                  transform: `scaleX(${Math.min(
                    1,
                    Math.max(0, (vpLastPixel - screenValues.tops[i]) / (vpLastPixel - vpCenter)),
                  )}`,
                }}
              />
            </div>
          ))}
        </div>
        <div className="px-5 pt-5 lg:px-20 md:pt-0 mx-auto max-w-7xl">
          <div className="text-center  mx-auto mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-landing-blue mb-4">
              Five steps from renting to owning
            </h2>
            <p className="font-serif text-lg md:text-2xl text-landing-blue/70">
              No deposit to move in. Every step is designed to get you mortgage-ready.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[0.82fr_1.18fr] gap-8 lg:gap-16 items-start ">
            <aside ref={cardRef} className="hidden lg:block sticky top-28 self-start">
              <div className="bg-white rounded-3xl p-9 shadow-xl border border-landing-gray">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-landing-orange">
                  Step {current.n} <span className="text-landing-darkGray normal-case">/ 5</span>
                </div>
                <div className="h-48 my-4 flex items-center justify-center">
                  <current.Icon width="100%" height="100%" />
                </div>
                <div className="text-2xl font-bold text-landing-blue mb-4 min-h-[2.2em]">
                  {current.title}
                </div>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FDEBDA] text-landing-orange font-bold text-sm">
                  <div className="flex justify-center items-center">
                    <TimeClock className="w-4 h-[14px] " />{" "}
                    <span className=" ml-1">{current.time}</span>
                  </div>
                </span>
                <div className="flex gap-2 mt-7">
                  {STEPS.map((_, i) => (
                    <div
                      key={i}
                      className="flex-1 h-[5px] rounded-full bg-landing-gray overflow-hidden"
                    >
                      <div
                        className="h-full bg-landing-orange rounded-full origin-left"
                        style={{
                          transform: `scaleX(${Math.min(
                            1,
                            Math.max(
                              0,
                              (vpLastPixel - screenValues.tops[i]) /
                                (vpLastPixel - screenValues.deskTopCardCentre),
                            ),
                          )})`,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex flex-col">
              {STEPS.map((step, i) => (
                <Element key={step.n} name={`${step.n}`}>
                  <div
                    ref={(el) => (refs.current[i] = el)}
                    className="py-8 lg:py-12 border-b border-landing-gray last:border-b-0"
                  >
                    <div className="lg:hidden flex items-center justify-center h-36 mb-4">
                      <step.Icon width="60%" height="100%" />
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="w-10 h-10 rounded-full bg-landing-orange text-white font-bold flex items-center justify-center flex-none">
                        {step.n}
                      </span>
                      <span className="text-xs font-black tracking-widest uppercase text-landing-darkGray">
                        Step {step.n}
                      </span>
                      <span className="ml-auto flex items-center gap-2 text-sm font-bold text-landing-orange">
                        <TimeClock className="w-4 h-[14px]" /> {step.time}
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold text-landing-blue mb-2">{step.title}</h3>
                    <p className="font-serif text-lg text-landing-blue/70 mb-3 max-w-[42ch]">
                      {step.lead}
                    </p>
                    <p className="text-base text-landing-blue/80 mb-5 text-justify">{step.body}</p>
                    <div className="mb-5">
                      <StepCtaLink cta={step.cta} />
                    </div>
                    <FaqAccordion
                      items={step.faqs}
                      elementPrefix={step.n === 1 ? "step1-faq" : undefined}
                      openIndex={step.n === 1 ? step1OpenFaq : undefined}
                      setOpenIndex={step.n === 1 ? setStep1OpenFaq : undefined}
                    />
                  </div>
                </Element>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Element>
  );
};

export default Journey;
